'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useDrawingStore } from '@/features/drawing/model';
import { cn } from '@/shared/lib/cn';
import { toast } from '@/shared/lib/toast';
import { LoadingSpinner } from '@/shared/ui/LoadingSpinner';
import { MediaSourceSelector } from '@/shared/ui/MediaSourceSelector';
import { isMobileDevice } from '@/shared/lib/device';
import { TextBlocksOverlay } from './TextBlocksOverlay';
import { BrushPreview } from './BrushPreview';
import { EditorToolbarTop, EditorToolbarBottom } from './EditorToolbar';
import { useCanvasTransform } from '../hooks/useCanvasTransform';
import { useCanvasEvents } from '../hooks/useCanvasEvents';
import { useTouchGestures } from '../hooks/useTouchGestures';
import { useResizeHandler } from '../hooks/useResizeHandler';

interface ControlEditorProps {
  angleImageUrl: string;
  initialControlUrl?: string;
  onSave: (blob: Blob) => void;
  onCancel: () => void;
}

export function ControlEditor({
  angleImageUrl,
  initialControlUrl,
  onSave,
  onCancel,
}: ControlEditorProps) {
  const tErrors = useTranslations('common.errors');
  const tMedia = useTranslations('mediaSource');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const minZoomRef = useRef<number>(0.1);
  const [isMobile, setIsMobile] = useState(false);
  const [originalImageOpacity] = useState(0.6);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [sourceSelectorOpen, setSourceSelectorOpen] = useState(false);

  // Get store state and actions
  const {
    tool,
    canvas,
    ui,
    initCanvas,
    setCanvasTransform,
    endDrawing,
    saveCanvas,
    importImage,
    exportCanvas,
    endSession,
    setActiveTextBlock,
  } = useDrawingStore();

  // Memoize canvas transform values
  const canvasTransformMemo = useMemo(
    () => canvas.transform,
    [canvas.transform.zoom, canvas.transform.pan.x, canvas.transform.pan.y]
  );

  // Check if mobile device
  useEffect(() => {
    setIsMobile(isMobileDevice());
  }, []);

  // Use custom hooks
  const {
    transformRef,
    screenToCanvas,
    handleZoomIn,
    handleZoomOut,
    repositionCanvasIfNeeded,
  } = useCanvasTransform({
    containerRef,
    canvasRef,
    minZoomRef,
    isMobile,
  });

  const { lastContainerDimensionsRef } = useResizeHandler({
    containerRef,
    canvasRef,
    minZoomRef,
    transformRef,
    isMobile,
  });

  const { handleMouseDown, getCursorStyle } = useCanvasEvents({
    containerRef,
    screenToCanvas,
    repositionCanvasIfNeeded,
  });

  useTouchGestures({
    canvasRef,
    containerRef,
    screenToCanvas,
    repositionCanvasIfNeeded,
    minZoomRef,
    transformRef,
    colorPickerOpen,
  });

  // Track initialization
  const initializedRef = useRef(false);

  // Cleanup: End session when component unmounts
  useEffect(() => {
    return () => {
      endSession(false);
    };
  }, [endSession]);

  // Initialize canvas with fit to window
  useEffect(() => {
    const canvasEl = canvasRef.current;
    const container = containerRef.current;
    if (!canvasEl || !container) return;

    if (initializedRef.current) {
      return;
    }

    initializedRef.current = true;

    const initAndFit = async () => {
      try {
        await initCanvas(canvasEl, angleImageUrl, initialControlUrl);

        const containerRect = container.getBoundingClientRect();
        const mobile = isMobileDevice();
        const padding = mobile ? 20 : 40;
        const availableWidth = containerRect.width - padding * 2;
        const availableHeight = containerRect.height - padding * 2;

        const scaleX = availableWidth / canvasEl.width;
        const scaleY = availableHeight / canvasEl.height;
        const fitScale = Math.min(scaleX, scaleY, 3);

        minZoomRef.current = Math.max(fitScale * 0.8, 0.1);

        lastContainerDimensionsRef.current = {
          width: containerRect.width,
          height: containerRect.height,
        };

        setCanvasTransform({
          zoom: fitScale,
          pan: { x: 0, y: 0 },
        });
      } catch (_error) {
        initializedRef.current = false;
        toast.error(tErrors('generic'));
        onCancel();
      }
    };

    initAndFit();

    return () => {
      initializedRef.current = false;
    };
  }, [
    angleImageUrl,
    initialControlUrl,
    initCanvas,
    setCanvasTransform,
    lastContainerDimensionsRef,
    onCancel,
  ]);

  // Save handler
  const handleSave = useCallback(async () => {
    try {
      const blob = await saveCanvas();
      onSave(blob);
    } catch (_error) {
      toast.error(tErrors('generic'));
    }
  }, [saveCanvas, onSave, tErrors]);

  // Export handler
  const handleExport = useCallback(async () => {
    try {
      const blob = await exportCanvas('png');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `control-${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (_error) {
      toast.error(tErrors('generic'));
    }
  }, [exportCanvas, tErrors]);

  // Import handler — opens multi-source picker (upload / existing renders)
  const handleImport = useCallback(() => {
    setSourceSelectorOpen(true);
  }, []);

  const handleSourceFilesSelected = useCallback(
    async (files: File[]) => {
      setSourceSelectorOpen(false);
      const file = files[0];
      if (!file) return;

      try {
        await importImage(file);
      } catch (_error) {
        toast.error(tErrors('generic'));
      }
    },
    [importImage, tErrors]
  );

  return (
    <div className="fixed inset-0 bg-black flex flex-col select-none">
      {/* Top Toolbar */}
      <EditorToolbarTop
        isMobile={isMobile}
        onCancel={onCancel}
        onSave={handleSave}
        onImport={handleImport}
        onExport={handleExport}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
      />

      <MediaSourceSelector
        isOpen={sourceSelectorOpen}
        onClose={() => setSourceSelectorOpen(false)}
        onFilesSelected={handleSourceFilesSelected}
        config={{
          showRendersOption: true,
          allowMultiple: false,
          accept: 'image/*',
          title: tMedia('titleUploadSketch'),
        }}
      />

      {/* Canvas Container */}
      <div
        ref={containerRef}
        className="flex-1 overflow-hidden bg-gray-200 relative select-none"
        style={{
          cursor: getCursorStyle(),
          pointerEvents: colorPickerOpen ? 'none' : 'auto',
          userSelect: 'none',
          WebkitUserSelect: 'none',
        }}
        onMouseDown={(e) => {
          if (e.button === 1) {
            e.preventDefault();
          }
          if (
            e.button === 0 &&
            e.target === e.currentTarget &&
            tool !== 'text'
          ) {
            setActiveTextBlock(null);
          }
        }}
        onTouchStart={(e) => {
          if (e.touches.length > 1) {
            return;
          }
          if (e.target !== e.currentTarget) {
            return;
          }
          if (tool !== 'text') {
            setActiveTextBlock(null);
          }
        }}
        onMouseLeave={() => {
          endDrawing();
        }}
      >
        <div className="absolute inset-0 flex items-center justify-center bg-black">
          <div
            className="pointer-events-none"
            style={{
              width: `${canvas.width}px`,
              height: `${canvas.height}px`,
              transform: `translate(${canvasTransformMemo.pan.x}px, ${canvasTransformMemo.pan.y}px) scale(${canvasTransformMemo.zoom})`,
              transformOrigin: 'center',
              position: 'relative',
              backgroundColor: 'white',
            }}
          >
            {/* Original image overlay */}
            {canvas.angleImageUrl && (
              <img
                src={canvas.angleImageUrl}
                alt="Original angle"
                draggable={false}
                className="pointer-events-none"
                style={{
                  width: '100%',
                  height: '100%',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  opacity: originalImageOpacity,
                  filter: 'saturate(0.25) brightness(0.95)',
                  imageRendering:
                    canvasTransformMemo.zoom > 2 ? 'pixelated' : 'auto',
                  zIndex: 1,
                }}
              />
            )}
            <canvas
              ref={canvasRef}
              className={cn(
                'shadow-lg pointer-events-auto select-none',
                ui.loading.image && 'opacity-50'
              )}
              style={{
                position: 'relative',
                zIndex: 2,
                imageRendering:
                  canvasTransformMemo.zoom > 2 ? 'pixelated' : 'auto',
                userSelect: 'none',
                WebkitUserSelect: 'none',
              }}
              onMouseDown={handleMouseDown}
            />
          </div>
        </div>

        {/* Brush preview */}
        {!isMobile && (
          <BrushPreview
            containerRef={containerRef}
            canvasTransform={canvasTransformMemo}
          />
        )}

        {/* Text blocks overlay */}
        <TextBlocksOverlay containerRef={containerRef} />
      </div>

      {/* Bottom Toolbar — tool selection + brush size + color (matches iOS / Android) */}
      <EditorToolbarBottom
        isMobile={isMobile}
        canvasTransform={canvasTransformMemo}
        colorPickerOpen={colorPickerOpen}
        onColorPickerOpenChange={setColorPickerOpen}
      />

      {/* Loading Overlay */}
      {ui.loading.image && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6">
            <LoadingSpinner size="lg" />
          </div>
        </div>
      )}
    </div>
  );
}
